/**
 * Constants.js
 * Centralized game constants
 */

/**
 * Pattern data from PatternGenerator
 * @typedef {Object} CollisionData
 * @property {Boolean} hit - Is there a hit?
 * @property {Boolean} hitBubble - Is there a hit with a bubble?
 * @property {Boolean} hitWall - Is there a hit with wall?
 * @property {Boolean} hitSideWall - Is there a hit with a side wall (top or bottom)?
 * @property {Boolean} hitBackWall - Is there a hit with the end (right) wall?
 * @property {Object} bubble - Bubble we are colliding with
 * @property {Number} contactX
 * @property {Number} contactY
 */

/**
 * Pattern data from PatternGenerator
 * @typedef {Object} GenerationConstraints
 * @property {Number} Act - The act this level is in, which will influence some graphical elements of the UI
 * @property {String[]} Respawn - The respawn pools associated with this level
 * @property {String} FirstBubble - The color of the first bubble placed in the shooter at the begining of the level
 * @property {Number} MaxConnected - The maximum number of bubbles of the same color that can be connected together at the start of the level
 * @property {Number} MinMatch - The minimum number of possible matches at the start of the level
 * @property {Number} MinMatchOf - The minimum number of bubbles of the same color that needs to be connected to count as a MinMatch
 * @property {Number} RedPercent - The spawn rate in % of this color
 * @property {Number} OrangePercent - The spawn rate in % of this color
 * @property {Number} YellowPercent - The spawn rate in % of this color
 * @property {Number} GreenPercent - The spawn rate in % of this color
 * @property {Number} BluePercent - The spawn rate in % of this color
 * @property {Number} PurplePercent - The spawn rate in % of this color
 * @property {Number} SpecialPercent - The spawn rate in % of any specials
 * @property {Number} HealPercent - The spawn rate in % of this special
 * @property {Number} StonePercent - The spawn rate in % of this special
 * @property {Number} ShieldedPercent - The spawn rate in % of this special
 * @property {Number} ColorlessPercent - The spawn rate in % of this special
 * @property {Number} ChameleonPercent - The spawn rate in % of this special
 */

/**
 * Tutorial save data
 * @typedef {Object} TutorialSaveData
 * @property {Boolean} Level1
 * @property {Boolean} Level2
 * @property {Boolean} AllOutAttack
 * @property {Boolean} HeroSelection
 * @property {Boolean} BubbleHeal
 * @property {Boolean} BubbleHidden
 * @property {Boolean} BubbleChameleon
 * @property {Boolean} BubbleStone
 * @property {Boolean} BubbleColorless
 * @property {Boolean} BubbleBlooming
 * @property {Boolean} BubbleBloomingStone
 */

/**
 * @typedef {Object} AnimationSettings
 * @property {Number} clearDisconnectedDelay
 * @property {Number} scatterDelay
 * @property {Number} scatterDelayMultiplier
 * @property {Number} scatterDistance
 * @property {Number} scatterDistanceRandomRange
 * @property {Number} scatterDuration
 * @property {Number} scatterPopDelay
 * @property {Number} scatterDirectionRandomRange
 * @property {Number} scatterDirectionMin
 * @property {Number} scatterDirectionMax
 * @property {Number} spawnDuration
 * @property {Number} spawnCascadeDelay
 * @property {Number} spawnCascadeSpeedUp
 * @property {Number} healDelay
 * @property {Number} buffTimer
 */

/**
 * @typedef {Object} SpaceManipulationSettings
 * @property {Number} probRegular
 * @property {Number} probHeal
 * @property {Number} probShield
 * @property {Number} probChameleon
 * @property {Number} probColorless
 * @property {Number} probStone
 * @property {Number} probBlooming
 * @property {Number} probStoneBlooming
 */

/**
 * @typedef {Object} AimLineSettings
 * @property {Number} length
 * @property {Number} reboundLength
 */

/**
 * @typedef {Object} BubbleSettings
 * @property {Number} lightningSpeed
 * @property {Number} lightningSpeedRandomRange
 * @property {Number} lightningPopDelay
 * @property {Number} lightningFadeDelay
 * @property {Number} thorRotationSpeed
 */

/**
 * @typedef {Object} AudioSettings
 * @property {Number} bubbleBurstOverlapDelay
 * @property {Number} bubbleBurstOverlapMaxDelay
 */

/**
 * @typedef {Object} LightningNode
 * @property {import("./game/Bubble").Bubble} bubble
 * @property {Number} distanceFromStart
 * @property {LightningNode[]} nextNodes
 */

/** @enum {String} */
export const GameEvents = Object.freeze({
    eATTACKPHASE_ABILITY_BEGIN: "AttackPhase:Ability:Begin",
    eATTACKPHASE_ATTACK_BEGIN: "AttackPhase:Attack:Begin",
    eATTACKPHASE_CUTIN_BEGIN: "AttackPhase:CutIn:Begin",
    eATTACKPHASE_ENEMY_ATTACK_COMPLETED: "AttackPhase:Enemy:Attack:Completed",
    eATTACKPHASE_FLASH_BEGIN: "AttackPhase:Flash:Begin",
    eATTACKPHASE_FLASH_COMPLETED: "AttackPhase:Flash:Completed",
    eATTACKPHASE_NEXT_ACTION: "AttackPhase:NextAction",

    eAVENGERS_ASSEMBLE: "Avengers:Assemble",
    eBOSS_CUT_IN_DONE: "Boss:CutInDone",
    eBOSS_INITIALIZED: "Boss:Initialized",
    eBOSS_SET_CUTIN: "Boss:SetCutIn",
    eBUBBLE_COLORLESS_MATCHED: "Bubble:ColorlessMatched",
    eBUBBLE_COUNTDOWN_EFFECT: "Bubble:CountdownEffect",
    eBUBBLE_CLEARED: "Bubbles:Cleared",
    eBUBBLE_POPPED: "Bubbles:Popped",
    eBUBBLE_SCATTER_DONE: "Bubble:ScatterDone",
    eCANNON_ABILITY_CHANGE_BUBBLE: "Cannon:Ability:ChangeBubble",
    eCANNON_RELOAD: "Cannon:Reload",
    eCANNON_SHOOT: "Cannon:Shoot",
    eCANNON_SWAP: "Cannon:Swap",
    eCANNON_RESERVE_CHANGED: "Cannon:Reserve:Changed",
    eCINEMATIC_PLAYED: "Cinematics:Played",
    eCINEMATIC_SET: "Cinematics:Set",
    eCINEMATIC_SKIP: "Cinematics:Skip",
    eDAMAGE_PARTICLE_ENEMY_HIT: "DamageParticle:EnemyHit",
    eENEMIES_ATTACK_PHASE: "Enemies:AttackPhase:Completed",

    eLIGHTNING_BUBBLES_CLEARED: "Lightning:BubblesCleared",

    eHERO_INFO_ABILITY: "Hero:Info:Ability",

    eGAME_ABILITY_ADD_BUBBLE: "Game:Ability:AddBubble",
    eGAME_ABILITY_ADD_OVERTIME: "Game:Ability:AddOvertime",
    eGAME_ABILITY_ADD_RANDOM_LINE: "Game:Ability:AddRandomLine",
    eGAME_ABILITY_CHARIOT: "Game:Ability:Chariot",
    eGAME_ABILITY_DOUBLE_EDGED_SWORD: "Game:Ability:DoubleEdgedSword",
    eGAME_ABILITY_DUPLICATE: "Game:Ability:Duplicate",
    eGAME_ABILITY_ENEMY_POWER_DOWN: "Game:Ability:EnemyPowerDown",
    eGAME_ABILITY_ENEMY_POWER_UP: "Game:Ability:EnemyPowerUp",
    eGAME_ABILITY_JAM: "Game:Ability:Jam",
    eGAME_ABILITY_HACKER: "Game:Ability:Hacker",
    eGAME_ABILITY_HEAVY_SLAM: "Game:Ability:HeavySlam",
    eGAME_ABILITY_MASS_PRODUCTION: "Game:Ability:MassProduction",
    eGAME_ABILITY_PROXIMAS_SPEAR: "Game:Ability:ProximasSpear",
    eGAME_ABILITY_RANDOM_SHOOTER: "Game:Ability:RandomShooter",
    eGAME_ABILITY_REALITY_SHIFT: "Game:Ability:RealityShift",
    eGAME_ABILITY_REGENERATION: "Game:Ability:Regeneration",
    eGAME_ABILITY_REPAIR: "Game:Ability:Repair",
    eGAME_ABILITY_SHIELD_ENEMY: "Game:Ability:ShieldEnemy",
    eGAME_ABILITY_SNAP: "Game:Ability:Snap",
    eGAME_ABILITY_SPACE_MANIPULATION: "Game:Ability:SpaceManipulation",
    eGAME_ABILITY_TRANSFORM_TO_SHIELD: "Game:Ability:TransformToShield",
    eGAME_ABILITY_TRANSFORM_TO_STONE: "Game:Ability:TransformToStone",
    eGAME_ABILITY_TROOPS_POD: "Game:Ability:TroopsPod",
    eGAME_ABILITY_UPGRADE_HEAL: "Game:Ability:UpgradeHeal",
    eGAME_ABILITY_UPGRADE_WEAKNESSES: "Game:Ability:UpgradeWeaknesses",

    eGAME_BUBBLE_DELAY_COUNTDOWN: "Game:BubbleDelayCountdown",
    eGAME_DAMAGE_ENEMY: "Game:DamageEnemy",
    eGAME_DISABLE_INPUTS: "Game:DisableInputs",
    eGAME_ENABLE_INPUTS: "Game:EnableInputs",
    eGAME_ENEMY_DEAD: "Game:EnemyDead",
    eGAME_HEAL_ENEMY: "Game:HealEnemy",
    eGAME_HIDE_ENEMIES: "Game:HideEnemies",
    eGAME_LOSE_LEVEL: "Game:LoseLevel",
    eGAME_NEXT_LEVEL: "Game:NextLevel",
    eGAME_REMOVE_ENEMY_SHIELD: "Game:RemoveEnemyShield",
    eGAME_SET_BUFF_TIMER: "Game:SetBuffTimer",
    eGAME_SET_ENEMY: "Game:SetEnemy",
    eGAME_SET_ENEMY_ATTACK_TIMER: "Game:SetEnemyAttackTimer",
    eGAME_SET_ENEMY_SHIELD_COUNTER: "Game:SetEnemyShieldCounter",
    eGAME_SET_WARNING_LINE_OPACITY: "Game:SetHurtLineOpacity",
    eGAME_SET_LEVEL: "Game:SetLevel",
    eGAME_SET_NUMBER_OF_ENEMIES: "Game:SetNumberOfEnemies",
    eGAME_SET_NUMBER_OVERTIME_TURNS: "Game:SetNumberOvertimeTurns",
    eGAME_SET_RANDOM_BUBBLES_COUNTER: "Game:SetRandomBubblesCounter",
    eGAME_SHIELD_ENEMIES: "Game:Shieldenemy",
    eGAME_SPAWN_BLOOMING: "Game:SpawnBlooming",
    eGAME_START_WAVE: "Game:StartWave",
    eGAME_START_WAVE_COMPLETED: "Game:StartWaveCompleted",
    eGAME_STOP_POWER_UP_BUFF_ANIMATION: "Game:StopPowerUpBuffAnimation",
    eGAME_STUN_ENEMY: "Game:StunEnemy",
    eGAME_TICKDOWN_COUNTDOWN: "Game:TickdownCountdown",
    eGAME_WALL_BOUNCE: "Game:WallBounce",
    eGAME_WIN_LEVEL: "Game:WinLevel",

    eWEAKNESS_CHANGE_SET_COLOR: "WeaknessChange:SetColor",

    eSWAP_HINT_BEGIN: "SwapHint:Begin",
    eSWAP_HINT_END: "SwapHint:End",

    eGAMEDATAMANAGER_READY: "GameDataManager:Ready",
    eGAMEDATAMANAGER_SAVEDATALOADED: "GameDataManager:SaveDataLoaded",
    eHERO_ABILITY_CHARGED: "Hero:Ability:Charged",
    eHERO_ABILITY_CHARGING: "Hero:Ability:Charging",
    eHERO_BUTTON_USE_ABILITY: "Hero:Button:UseAbility",
    eHERO_USE_ABILITY: "Hero:UseAbility",
    eHERO_ABILITY_BLACK_PANTHER_ACTIVATE: "HeroAbility:BlackPanther:Activate",
    eHERO_ABILITY_BLACK_PANTHER_ATTACK: "HeroAbility:BlackPanther:Attack",
    eHERO_ABILITY_BLACK_PANTHER_SETUP: "HeroAbility:BlackPanther:Setup",
    eHERO_ABILITY_BLACK_WIDOW: "HeroAbility:BlackWidow",
    eHERO_ABILITY_CAPTAIN_AMERICA: "HeroAbility:CaptainAmerica",
    eHERO_ABILITY_CAPTAIN_AMERICA_DONE: "HeroAbility:CaptainAmericaDone",
    eHERO_ABILITY_WASP: "HeroAbility:Wasp",

    eHERO_SELECT_OPEN: "HeroSelect:Open",
    eHERO_SELECT_CONFIRM: "HeroSelect:Confirm",
    eHERO_SELECT_TEAMUP: "HeroSelect:TeamUp",

    eHEROES_ALL_OUT_ATTACK: "Heroes:AllOutAttack",
    eHEROES_ALL_OUT_ATTACK_CUTIN_DONE: "Heroes:AlloutAttack:CutIn:Done",
    eHEROES_ALL_OUT_ATTACK_BUFF_COMPLETED: "Heroes:AllOutAttack:Buff:Completed",
    eHEROES_DAMAGED: "Heroes:Damaged",
    eHEROES_DEAD: "Heroes:Dead",
    eHEROES_HEAL_BUBBLE: "Heroes:HealBubble",
    eHEROES_HEALED: "Heroes:Healed",
    eHEROES_HIT: "Heroes:Hit",
    eHEROES_REMOVE_STUN: "Heroes:RemoveStun",
    eHEROES_STUNNED: "Heroes:Stunned",
    eHEROES_STUNNED_VISUAL: "Heroes:StunnedVisual",
    eHEROES_INITIALIZED: "Heroes:Initialized",
    eHEROES_ABILITY_DURATION_TICK: "HeroesAbility:DurationTick",

    eOPEN_ABILITY_POPUP: "Open:AbilityPopup",

    eHUD_HIDE_POPUPS: "HUD:HidePopups",
    eLEVEL_MANAGER_GAME_PROGRESS: "LevelManager:GameProgress",

    eLEVEL_SELECTION_SET_LEVEL: "LevelSelection:SetLevel",
    eLEVEL_SELECTION_UNLOCK_ALL_LEVELS: "LevelSelection:UnlockAllLevels",
    eLEVEL_SELECTION_UNLOCK_ALL_LEVELS_CONFIRMED: "LevelSelection:UnlockAllLevels:Confirmed",

    eREQUEST_ACTIVE_ENEMIES: "Request:ActiveEnemies",
    eRESET_BUTTON_INPUTS: "ResetButtonInputs",
    eRESULTS_LEVELS: "Results:Levels",
    eRESULTS_NEXT: "Results:Next",
    eRESULTS_RESTART: "Results:Restart",
    eSETTINGSMANAGER_READY: "SettingsManager:Ready",
    eSHOT_MATCH: "Shot:Match",
    eTITLE_PLAY: "Title:Play",
    eTITLE_RESET: "Title:Reset",
    eTUTORIAL_HAND_POS: "Tutorial:HandPos",
    eTUTORIAL_STEP_PLAYED: "TutorialStep:Played",

    eTUTORIAL_HIGHLIGHT_AIM: "Tutorial:Highlight:Aim",
    eTUTORIAL_HIGHLIGHT_WEAKNESSES: "Tutorial:Highlight:Weaknesses",
    eTUTORIAL_HIGHLIGHT_ATTACK_TIMERS: "Tutorial:Highlight:AttackTimers",
    eTUTORIAL_HIGHLIGHT_CANNON: "Tutorial:Highlight:Cannon",
    eTUTORIAL_HIGHLIGHT_HERO_COUNTERS: "Tutorial:Highlight:HeroCounters",
    eTUTORIAL_HIGHLIGHT_HERO_ABILITIES: "Tutorial:Highlight:HeroAbilities",
    eTUTORIAL_HIGHLIGHT_HERO_ACTIVATION: "Tutorial:Highlight:HeroActivation",
    eTUTORIAL_HIGHLIGHT_CHANGE_TEAM: "Tutorial:Highlight:ChangeTeam",
    eTUTORIAL_HIGHLIGHT_SWITCH_AVENGERS: "Tutorial:Highlight:SwitchAvengers",
    eTUTORIAL_HIGHLIGHT_CLEAR: "Tutorial:Highlight:Clear",
    eTUTORIAL_RESET_STEPS: "Tutorial:ResetSteps"
});

/** @enum {String} */
export const CheatEvents = Object.freeze({
    eAVENGERS_INFINITE_ABILITY: "Cheats:AvengersInfiniteAbility",
    eAVENGERS_INVINCIBLE: "Cheats:AvengersInvincible",
    eCOMPLETE_LEVEL: "Cheats:CompleteLevel",
    eDAMAGE_MULTIPLIER: "Cheats:DamageMultiplier",
    eDEPLETE_ENEMIES: "Cheats:DepleteEnemies",
    eALL_OUT_COMPOSITION: "Cheats:AllOutComposition",
    eENEMY_ALWAYS_ATTACK: "Cheats:EnemyAlwaysAttack",
    eFAIL_LEVEL: "Cheats:FailLevel",
    eFORCE_BUBBLE_COLOR: "Cheats:ForceBubbleColor",
    eRECEIVE_DAMAGE: "Cheats:ReceiveDamage",
    eSPAWN_BUBBLE_COLOR: "Cheats:SpawnBubbleColor",
    eLEVEL_SELECTION_SET_LEVEL_DEBUG: "LevelSelection:SetLevel:Debug"
});

/** @enum {String} */
export const ButtonGroups = Object.freeze({
    eTITLE: "Title",
    eCHANGE_TEAM: "ChangeTeam",
    eLEVELS: "Levels",
    eUNLOCK_ALL: "UnlockAll",
    eAVENGERS: "Avengers",
    eRESULTS: "Results"
});

/** @enum {String} */
export const HelpPages = Object.freeze({
    eCONTROLS_TOUCH: "HelpControlsTouch",
    eCONTROLS_KEYS: "HelpControlsKeys",
    eCONTROLS_IMAGES_TOUCH: "HelpControlsImagesTouch",
    eCONTROLS_IMAGES_KEYBOARD: "HelpControlsImagesKeyboard",
    eCONTROLS_IMAGES_HANDSET: "HelpControlsImagesHandset",
    eHOW_TO_PLAY: "HelpHowToPlay",
    eABILITIES: "HelpAbilities",
    eAVENGERS: "HelpAvengers",
    eBUBBLES: "HelpBubbles",
    eSTATUS_EFFECTS: "HelpStatusEffects",
    eCREDITS: "HelpCredits"
});

export const HELP_MARGIN_TOP = 20;
export const HELP_MARGIN_BOTTOM = 50;
export const HELP_CREDITS_AUTOSCROLL_SPEED = -50;
export const HELP_CREDITS_AUTOSCROLL_OFFSET = 300;
export const HELP_CREDITS_MARGIN = 20;

/** @enum {String} */
export const SceneLayers = Object.freeze({
    eBACKGROUND: "Background",
    eWALLS: "Walls",
    eAIM_LINE: "AimLine",
    eBUBBLES: "Bubbles",
    eBUBBLES_DROPPED: "BubblesDropped",
    ePARTICLES: "Particles",
    eBUBBLE_OVER: "BubbleOver",
    eBUBBLE_FIRED: "BubbleFired",
    eOVER_BUBBLES_UI: "OverBubblesUI"
});

export const AUDIO_BUTTONS = Object.freeze({
    musicOn: "BTN_Music",
    musicOnOver: "BTN_MusicHover",
    musicOnPressed: "BTN_MusicPressed",
    musicOff: "BTN_MusicOff",
    musicOffOver: "BTN_MusicOffHover",
    musicOffPressed: "BTN_MusicOffPressed",
    soundOn: "BTN_Sound",
    soundOnOver: "BTN_SoundHover",
    soundOnPressed: "BTN_SoundPressed",
    soundOff: "BTN_SoundOff",
    soundOffOver: "BTN_SoundOffHover",
    soundOffPressed: "BTN_SoundOffPressed"
});

export const REF_RESOLUTION_WIDTH = 1024;
export const REF_RESOLUTION_HEIGHT = 640;

export const CLEAR_COLOR_TITLE = 0x010303;

export const CAMERA_TARGET_HEIGHT = 640;
export const CAMERA_MIN_WIDTH = 1024;

export const OPTIONS_PREFAB = "Options";
export const SETTINGS_PREFAB = "Settings";
export const PAUSE_PREFAB = "Pause";

export const MUSIC_FADE_IN_TIME = 1; // seconds
export const MUSIC_FADE_OUT_TIME = 1; // seconds

export const SHORT_DELAY = 100;
export const DEFAULT_HEAL_ANIMATION_DELAY = 600; // ms
export const CANNON_SWAP_ANIM_DELAY = 1800; // ms
export const DEFAULT_BUFF_TIMER = 500; //ms

export const TEAM_SIZE = 4;
export const NB_MAX_ENEMIES = 3;
export const NB_LEVELS = 20;
export const NB_COLORED_BUBBLES = 6;
export const NB_HEROES = 8;
export const NB_MAX_ENEMY_WEAKNESSES = 3;
export const NB_ENEMY_TRACKED_TURNS = 4;
export const TOUCH_TEAM_BUTTON_Y_OFFSET = -60;

export const NB_MAX_COLUMN = 18;
export const NB_MAX_ROW = 15;
export const NB_MAX_COLUMN_INITIAL_SETUP = 10;

export const NB_COLOR_MATCH = 3;

export const GRID_STARTING_OFFSET = { x: 295, y: -228 };
export const GRID_BUBBLE_OFFSET = { x: 30, y: 34 };

export const SCALE_PRESSED = 0.95;
export const ENEMY_SHIELD_AND_CHARGE_DISTANCE = -30;
export const ENEMY_TIMER_FLASH_SCALE = 2;
export const ENEMY_TIMER_FLASH_TIME = 400; //ms
export const ENEMY_TIMER_COLOR_FLASH_TIME = 500; //ms

export const ENEMY_UI_VISIBILITY = [
    [false, true, false],
    [true, false, true],
    [true, true, true]
];

export const CLEAR_COLOR_INGAME = {
    [0]: 0x000000,
    [1]: 0x000000,
    [2]: 0x000000,
    [3]: 0x000000
};

export const HUD_HERO_BOX_WIDTH = 124;
export const HUD_HERO_BOX_HEIGHT = 120;
export const HUD_HERO_BOX_BOTTOM = 60;
export const HUD_HERO_BOX_CHARGE_ANIMATION_DURATION = 0.5; // sec

export const HUD_ENEMIES_MASK_OFFSET_X = -100;
export const HUD_ENEMIES_MASK_WIDTH = 400;

export const DEFAULT_DRAG_THRESHOLD = 5; // pixels

export const ABILITY_POPUP_Y = {
    [0]: -170,
    [1]: -40,
    [2]: 90,
    [3]: 220
};

/** @enum {String} */
export const GameDataIds = Object.freeze({
    eGAME: "pxmrvpop:GameSaveData",
    eCINEMATIC: "pxmrvpop:CinematicData",
    eTUTORIAL: "pxmrvpop:TutorialData"
});

/** @enum {String} */
export const Heroes = Object.freeze({
    eIRON_MAN: "IronMan",
    eBLACK_PANTHER: "BlackPanther",
    eHULK: "Hulk",
    eTHOR: "Thor",
    eCAPTAIN_AMERICA: "CaptainAmerica",
    eBLACK_WIDOW: "BlackWidow",
    eCAPTAIN_MARVEL: "CaptainMarvel",
    eWASP: "Wasp"
});

/** @enum {Number} */
export const HeroesId = Object.freeze({
    eHULK: 0,
    eTHOR: 1,
    eCAPTAIN_MARVEL: 2,
    eWASP: 3,
    eBLACK_PANTHER: 4,
    eBLACK_WIDOW: 5,
    eCAPTAIN_AMERICA: 6,
    eIRON_MAN: 7
});

export const HEROES_ID_TO_NAME = {
    [HeroesId.eIRON_MAN]: "IronMan",
    [HeroesId.eBLACK_PANTHER]: "BlackPanther",
    [HeroesId.eHULK]: "Hulk",
    [HeroesId.eTHOR]: "Thor",
    [HeroesId.eCAPTAIN_AMERICA]: "CaptainAmerica",
    [HeroesId.eBLACK_WIDOW]: "BlackWidow",
    [HeroesId.eCAPTAIN_MARVEL]: "CaptainMarvel",
    [HeroesId.eWASP]: "Wasp"
};

export const HEROES_NAME_TO_ID = {
    ["IronMan"]: HeroesId.eIRON_MAN,
    ["BlackPanther"]: HeroesId.eBLACK_PANTHER,
    ["Hulk"]: HeroesId.eHULK,
    ["Thor"]: HeroesId.eTHOR,
    ["CaptainAmerica"]: HeroesId.eCAPTAIN_AMERICA,
    ["BlackWidow"]: HeroesId.eBLACK_WIDOW,
    ["CaptainMarvel"]: HeroesId.eCAPTAIN_MARVEL,
    ["Wasp"]: HeroesId.eWASP
};

export const HERO_NAMES = Object.freeze({
    [Heroes.eIRON_MAN]: "IDS_TXT_HERO_IRON_MAN_NAME",
    [Heroes.eBLACK_PANTHER]: "IDS_TXT_HERO_BLACK_PANTHER_NAME",
    [Heroes.eHULK]: "IDS_TXT_HERO_HULK_NAME",
    [Heroes.eTHOR]: "IDS_TXT_HERO_THOR_NAME",
    [Heroes.eCAPTAIN_AMERICA]: "IDS_TXT_HERO_CAPTAIN_AMERICA_NAME",
    [Heroes.eBLACK_WIDOW]: "IDS_TXT_HERO_BLACK_WIDOW_NAME",
    [Heroes.eCAPTAIN_MARVEL]: "IDS_TXT_HERO_CAPTAIN_MARVEL_NAME",
    [Heroes.eWASP]: "IDS_TXT_HERO_THE_WASP_NAME"
});

export const HERO_BUTTON_SPRITES = Object.freeze({
    [Heroes.eIRON_MAN]: "BTN_SideBarIronMan",
    [Heroes.eBLACK_PANTHER]: "BTN_SideBarBlackPanther",
    [Heroes.eHULK]: "BTN_SideBarHulk",
    [Heroes.eTHOR]: "BTN_SideBarThor",
    [Heroes.eCAPTAIN_AMERICA]: "BTN_SideBarCptnAmerica",
    [Heroes.eBLACK_WIDOW]: "BTN_SideBarBlackWidow",
    [Heroes.eCAPTAIN_MARVEL]: "BTN_SideBarCptnMarvel",
    [Heroes.eWASP]: "BTN_SideBarWasp"
});

export const HERO_BUTTON_SPRITES_OVER = Object.freeze({
    [Heroes.eIRON_MAN]: "BTN_SideBarIronManHover",
    [Heroes.eBLACK_PANTHER]: "BTN_SideBarBlackPantherHover",
    [Heroes.eHULK]: "BTN_SideBarHulkHover",
    [Heroes.eTHOR]: "BTN_SideBarThorHover",
    [Heroes.eCAPTAIN_AMERICA]: "BTN_SideBarCptnAmericaHover",
    [Heroes.eBLACK_WIDOW]: "BTN_SideBarBlackWidowHover",
    [Heroes.eCAPTAIN_MARVEL]: "BTN_SideBarCptnMarvelHover",
    [Heroes.eWASP]: "BTN_SideBarWaspHover"
});

export const HERO_BUTTON_SPRITES_PRESSED = Object.freeze({
    [Heroes.eIRON_MAN]: "BTN_SideBarIronManPressed",
    [Heroes.eBLACK_PANTHER]: "BTN_SideBarBlackPantherPressed",
    [Heroes.eHULK]: "BTN_SideBarHulkPressed",
    [Heroes.eTHOR]: "BTN_SideBarThorPressed",
    [Heroes.eCAPTAIN_AMERICA]: "BTN_SideBarCptnAmericaPressed",
    [Heroes.eBLACK_WIDOW]: "BTN_SideBarBlackWidowPressed",
    [Heroes.eCAPTAIN_MARVEL]: "BTN_SideBarCptnMarvelPressed",
    [Heroes.eWASP]: "BTN_SideBarWaspPressed"
});

export const HERO_BUTTON_SPRITES_SELECTED = Object.freeze({
    [Heroes.eIRON_MAN]: "BTN_SideBarIronManSelect",
    [Heroes.eBLACK_PANTHER]: "BTN_SideBarBlackPantherSelect",
    [Heroes.eHULK]: "BTN_SideBarHulkSelect",
    [Heroes.eTHOR]: "BTN_SideBarThorSelect",
    [Heroes.eCAPTAIN_AMERICA]: "BTN_SideBarCptnAmericaSelect",
    [Heroes.eBLACK_WIDOW]: "BTN_SideBarBlackWidowSelect",
    [Heroes.eCAPTAIN_MARVEL]: "BTN_SideBarCptnMarvelSelect",
    [Heroes.eWASP]: "BTN_SideBarWaspSelect"
});

export const HERO_AVAILABLE_SPRITE = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_IronManIdle",
    [Heroes.eBLACK_PANTHER]: "IMG_BlackPantherIdle",
    [Heroes.eHULK]: "IMG_HulkIdle",
    [Heroes.eTHOR]: "IMG_ThorIdle",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_CptnAmericaIdle",
    [Heroes.eBLACK_WIDOW]: "IMG_BlackWidowIdle",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_CptnMarvelIdle",
    [Heroes.eWASP]: "IMG_WaspIdle"
});

export const HERO_AVAILABLE_SPRITE_OVER = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_IronManHover",
    [Heroes.eBLACK_PANTHER]: "IMG_BlackPantherHover",
    [Heroes.eHULK]: "IMG_HulkHover",
    [Heroes.eTHOR]: "IMG_ThorHover",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_CptnAmericaHover",
    [Heroes.eBLACK_WIDOW]: "IMG_BlackWidowHover",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_CptnMarvelHover",
    [Heroes.eWASP]: "IMG_WaspHover"
});

export const HERO_AVAILABLE_SPRITE_SELECTED = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_IronManSelected",
    [Heroes.eBLACK_PANTHER]: "IMG_BlackPantherSelected",
    [Heroes.eHULK]: "IMG_HulkSelected",
    [Heroes.eTHOR]: "IMG_ThorSelected",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_CptnAmericaSelected",
    [Heroes.eBLACK_WIDOW]: "IMG_BlackWidowSelected",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_CptnMarvelSelected",
    [Heroes.eWASP]: "IMG_WaspSelected"
});

export const HERO_AVAILABLE_SPRITE_CHOSEN = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_IronManChosen",
    [Heroes.eBLACK_PANTHER]: "IMG_BlackPantherChosen",
    [Heroes.eHULK]: "IMG_HulkChosen",
    [Heroes.eTHOR]: "IMG_ThorChosen",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_CptnAmericaChosen",
    [Heroes.eBLACK_WIDOW]: "IMG_BlackWidowChosen",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_CptnMarvelChosen",
    [Heroes.eWASP]: "IMG_WaspChosen"
});

export const HERO_AVAILABLE_ABILITY = Object.freeze({
    [Heroes.eIRON_MAN]: "ICN_IronManIdle",
    [Heroes.eBLACK_PANTHER]: "ICN_BlackPantherIdle",
    [Heroes.eHULK]: "ICN_HulkIdle",
    [Heroes.eTHOR]: "ICN_ThorIdle",
    [Heroes.eCAPTAIN_AMERICA]: "ICN_CptnAmericaIdle",
    [Heroes.eBLACK_WIDOW]: "ICN_BlackWidowIdle",
    [Heroes.eCAPTAIN_MARVEL]: "ICN_CptnMarvelIdle",
    [Heroes.eWASP]: "ICN_WaspIdle"
});

export const HERO_AVAILABLE_ABILITY_OVER = Object.freeze({
    [Heroes.eIRON_MAN]: "ICN_IronManHover",
    [Heroes.eBLACK_PANTHER]: "ICN_BlackPantherHover",
    [Heroes.eHULK]: "ICN_HulkHover",
    [Heroes.eTHOR]: "ICN_ThorHover",
    [Heroes.eCAPTAIN_AMERICA]: "ICN_CptnAmericaHover",
    [Heroes.eBLACK_WIDOW]: "ICN_BlackWidowHover",
    [Heroes.eCAPTAIN_MARVEL]: "ICN_CptnMarvelHover",
    [Heroes.eWASP]: "ICN_WaspHover"
});

export const HERO_AVAILABLE_ABILITY_SELECTED = Object.freeze({
    [Heroes.eIRON_MAN]: "ICN_IronManSelected",
    [Heroes.eBLACK_PANTHER]: "ICN_BlackPantherSelected",
    [Heroes.eHULK]: "ICN_HulkSelected",
    [Heroes.eTHOR]: "ICN_ThorSelected",
    [Heroes.eCAPTAIN_AMERICA]: "ICN_CptnAmericaSelected",
    [Heroes.eBLACK_WIDOW]: "ICN_BlackWidowSelected",
    [Heroes.eCAPTAIN_MARVEL]: "ICN_CptnMarvelSelected",
    [Heroes.eWASP]: "ICN_WaspSelected"
});

export const HERO_AVAILABLE_ABILITY_CHOSEN = Object.freeze({
    [Heroes.eIRON_MAN]: "ICN_IronManChosen",
    [Heroes.eBLACK_PANTHER]: "ICN_BlackPantherChosen",
    [Heroes.eHULK]: "ICN_HulkChosen",
    [Heroes.eTHOR]: "ICN_ThorChosen",
    [Heroes.eCAPTAIN_AMERICA]: "ICN_CptnAmericaChosen",
    [Heroes.eBLACK_WIDOW]: "ICN_BlackWidowChosen",
    [Heroes.eCAPTAIN_MARVEL]: "ICN_CptnMarvelChosen",
    [Heroes.eWASP]: "ICN_WaspChosen"
});

export const HERO_AVAILABLE_FRAME = Object.freeze({
    ["idle"]: "FRM_Characters",
    ["over"]: "FRM_CharactersHover",
    ["chosen"]: "FRM_CharactersChosen",
    ["selected"]: "FRM_CharactersSelected"
});

export const HERO_AVAILABLE_BACKGROUND = Object.freeze({
    ["idle"]: "IMG_CharactersBack",
    ["over"]: "IMG_CharactersBackHover",
    ["pressed"]: "IMG_CharactersBack",
    ["chosen"]: "IMG_CharactersChosen",
    ["selected"]: "IMG_CharactersSelected"
});

export const HERO_FEATURED_SPRITE = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_FeatureIronMan",
    [Heroes.eBLACK_PANTHER]: "IMG_FeatureBlackPanther",
    [Heroes.eHULK]: "IMG_FeatureHulk",
    [Heroes.eTHOR]: "IMG_FeatureThor",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_FeatureCptnAmerica",
    [Heroes.eBLACK_WIDOW]: "IMG_FeatureBlackWidow",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_FeatureCptnMarvel",
    [Heroes.eWASP]: "IMG_FeatureWasp"
});

export const HERO_FEATURED_ABILITY_ICON = Object.freeze({
    [Heroes.eIRON_MAN]: "ICN_FeatureIronMan",
    [Heroes.eBLACK_PANTHER]: "ICN_FeatureBlackPanther",
    [Heroes.eHULK]: "ICN_FeatureHulk",
    [Heroes.eTHOR]: "ICN_FeatureThor",
    [Heroes.eCAPTAIN_AMERICA]: "ICN_FeatureCptnAmerica",
    [Heroes.eBLACK_WIDOW]: "ICN_FeatureBlackWidow",
    [Heroes.eCAPTAIN_MARVEL]: "ICN_FeatureCptnMarvel",
    [Heroes.eWASP]: "ICN_FeatureWasp"
});

export const HERO_FEATURED_ABILITY_PREVIEW = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_FeatureIngameIronMan",
    [Heroes.eBLACK_PANTHER]: "IMG_FeatureIngameBlackPanther",
    [Heroes.eHULK]: "IMG_FeatureIngameHulk",
    [Heroes.eTHOR]: "IMG_FeatureIngameThor",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_FeatureIngameCptnAmerica",
    [Heroes.eBLACK_WIDOW]: "IMG_FeatureIngameBlackWidow",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_FeatureIngameCptnMarvel",
    [Heroes.eWASP]: "IMG_FeatureIngameWasp"
});

export const HERO_GAMEPLAY_SPRITES = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_IronMan",
    [Heroes.eBLACK_PANTHER]: "IMG_BlackPanther",
    [Heroes.eHULK]: "IMG_Hulk",
    [Heroes.eTHOR]: "IMG_Thor",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_CptnAmerica",
    [Heroes.eBLACK_WIDOW]: "IMG_BlackWidow",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_CptnMarvel",
    [Heroes.eWASP]: "IMG_Wasp"
});

export const HERO_DAMAGED_SPRITES = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_IronManHurt",
    [Heroes.eBLACK_PANTHER]: "IMG_BlackPantherHurt",
    [Heroes.eHULK]: "IMG_HulkHurt",
    [Heroes.eTHOR]: "IMG_ThorHurt",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_CptnAmericaHurt",
    [Heroes.eBLACK_WIDOW]: "IMG_BlackWidowHurt",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_CptnMarvelHurt",
    [Heroes.eWASP]: "IMG_WaspHurt"
});

export const HERO_LEVEL_SELECTION_SPRITES = Object.freeze({
    [Heroes.eIRON_MAN]: "BTN_SideBarIronMan",
    [Heroes.eBLACK_PANTHER]: "BTN_SideBarBlackPanther",
    [Heroes.eHULK]: "BTN_SideBarHulk",
    [Heroes.eTHOR]: "BTN_SideBarThor",
    [Heroes.eCAPTAIN_AMERICA]: "BTN_SideBarCptnAmerica",
    [Heroes.eBLACK_WIDOW]: "BTN_SideBarBlackWidow",
    [Heroes.eCAPTAIN_MARVEL]: "BTN_SideBarCptnMarvel",
    [Heroes.eWASP]: "BTN_SideBarWasp"
});

/** @enum {String} */
export const HERO_ABILITY_SPRITE = Object.freeze({
    [Heroes.eIRON_MAN]: "ICN_PowerIronMan",
    [Heroes.eBLACK_PANTHER]: "ICN_PowerBlackPanther",
    [Heroes.eHULK]: "ICN_PowerHulk",
    [Heroes.eTHOR]: "ICN_PowerThor",
    [Heroes.eCAPTAIN_AMERICA]: "ICN_PowerCpntAmerica",
    [Heroes.eBLACK_WIDOW]: "ICN_PowerBlackWidow",
    [Heroes.eCAPTAIN_MARVEL]: "ICN_PowerCptnMarvel",
    [Heroes.eWASP]: "ICN_PowerWasp"
});

/** @enum {String} */
export const HERO_ABILITY_IMAGES = Object.freeze({
    [Heroes.eIRON_MAN]: "IMG_PopUpAbilityIronMan",
    [Heroes.eBLACK_PANTHER]: "IMG_PopUpAbilityBlackPanther",
    [Heroes.eHULK]: "IMG_PopUpAbilityHulk",
    [Heroes.eTHOR]: "IMG_PopUpAbilityThor",
    [Heroes.eCAPTAIN_AMERICA]: "IMG_PopUpAbilityCptnAmerica",
    [Heroes.eBLACK_WIDOW]: "IMG_PopUpAbilityBlackWidow",
    [Heroes.eCAPTAIN_MARVEL]: "IMG_PopUpAbilityCptnMarvel",
    [Heroes.eWASP]: "IMG_PopUpAbilityWasp"
});

/** @enum {String} */
export const HERO_ABILITY_NAMES = Object.freeze({
    [Heroes.eIRON_MAN]: "IDS_TXT_HERO_IRON_MAN_ABILITY",
    [Heroes.eBLACK_PANTHER]: "IDS_TXT_HERO_BLACK_PANTHER_ABILITY",
    [Heroes.eHULK]: "IDS_TXT_HERO_HULK_ABILITY",
    [Heroes.eTHOR]: "IDS_TXT_HERO_THOR_ABILITY",
    [Heroes.eCAPTAIN_AMERICA]: "IDS_TXT_HERO_CAPTAIN_AMERICA_ABILITY",
    [Heroes.eBLACK_WIDOW]: "IDS_TXT_HERO_BLACK_WIDOW_ABILITY",
    [Heroes.eCAPTAIN_MARVEL]: "IDS_TXT_HERO_CAPTAIN_MARVEL_ABILITY",
    [Heroes.eWASP]: "IDS_TXT_HERO_THE_WASP_ABILITY"
});

/** @enum {String} */
export const HERO_ABILITY_DESCS = Object.freeze({
    [Heroes.eIRON_MAN]: "IDS_TXT_HERO_IRON_MAN_ABILITY_DESCRIPTION",
    [Heroes.eBLACK_PANTHER]: "IDS_TXT_HERO_BLACK_PANTHER_ABILITY_DESCRIPTION",
    [Heroes.eHULK]: "IDS_TXT_HERO_HULK_ABILITY_DESCRIPTION",
    [Heroes.eTHOR]: "IDS_TXT_HERO_THOR_ABILITY_DESCRIPTION",
    [Heroes.eCAPTAIN_AMERICA]: "IDS_TXT_HERO_CAPTAIN_AMERICA_ABILITY_DESCRIPTION",
    [Heroes.eBLACK_WIDOW]: "IDS_TXT_HERO_BLACK_WIDOW_ABILITY_DESCRIPTION",
    [Heroes.eCAPTAIN_MARVEL]: "IDS_TXT_HERO_CAPTAIN_MARVEL_ABILITY_DESCRIPTION",
    [Heroes.eWASP]: "IDS_TXT_HERO_THE_WASP_ABILITY_DESCRIPTION"
});

/** @enum {String} */
export const Enemies = Object.freeze({
    eRED_ROOM_GUARD_A: "RedRoomGuardA",
    eRED_ROOM_GUARD_B: "RedRoomGuardB",
    eRED_ROOM_GUARD_C: "RedRoomGuardC",
    eLOKI_WEAKENED: "LokiWeakened",
    eCHITAURI_ARTIFICIER: "ChitauriArtificier",
    eCHITAURI_FIGHTER: "ChitauriFighter",
    eCHITAURI_RIDER: "ChitauriRider",
    eLOKI: "Loki",
    eLOKI_COPY: "LokiCopy",
    eULTRONS_SENTRIES: "UltronsSentries",
    eHEAVY_ULTRONS_SENTRIES: "HeavyUltronsSentries",
    eSUPPORT_ULTRONS_SENTRIES: "SupportUltronsSentries",
    eLEVIATHAN: "Leviathan",
    eULTRON: "Ultron",
    eEBONY_MAW: "EbonyMaw",
    ePROXIMA_MIDNIGHT: "ProximaMidnight",
    eCORVUS_GLAIVE: "CorvusGlaive",
    eCULL_OBSIDIAN: "CullObsidian",
    eTHANOS: "Thanos",
    eNONE: "none"
});

/** @enum {String} */
export const EnemySprite = Object.freeze({
    [Enemies.eRED_ROOM_GUARD_A]: "IMG_RedRoomGuard3.png",
    [Enemies.eRED_ROOM_GUARD_B]: "IMG_RedRoomGuard2.png",
    [Enemies.eRED_ROOM_GUARD_C]: "IMG_RedRoomGuard1.png",
    [Enemies.eLOKI_WEAKENED]: "IMG_Loki.png",
    [Enemies.eCHITAURI_ARTIFICIER]: "IMG_ChitauriArtificer.png",
    [Enemies.eCHITAURI_FIGHTER]: "IMG_ChitauriFighter.png",
    [Enemies.eCHITAURI_RIDER]: "IMG_ChitauriRider.png",
    [Enemies.eLOKI]: "IMG_Loki.png",
    [Enemies.eLOKI_COPY]: "IMG_LokiClone.png",
    [Enemies.eULTRONS_SENTRIES]: "IMG_UltronSentry.png",
    [Enemies.eHEAVY_ULTRONS_SENTRIES]: "IMG_UltronHeavy.png",
    [Enemies.eSUPPORT_ULTRONS_SENTRIES]: "IMG_UltronSupport.png",
    [Enemies.eLEVIATHAN]: "IMG_Leviathan.png",
    [Enemies.eULTRON]: "IMG_Ultron.png",
    [Enemies.eEBONY_MAW]: "IMG_EbonyMaw.png",
    [Enemies.ePROXIMA_MIDNIGHT]: "IMG_ProximaMidnight.png",
    [Enemies.eCORVUS_GLAIVE]: "IMG_CorvusGlaive.png",
    [Enemies.eCULL_OBSIDIAN]: "IMG_CullObsidian.png",
    [Enemies.eTHANOS]: "IMG_Thanos.png",
    [Enemies.eNONE]: "none"
});

/** @enum {String} */
export const BossAttackSprite = Object.freeze({
    [Enemies.eLOKI_WEAKENED]: "IMG_LokiAttack.png",
    [Enemies.eLOKI]: "IMG_LokiAttack.png",
    [Enemies.eLOKI_COPY]: "IMG_LokiCloneAttack.png",
    [Enemies.eULTRON]: "IMG_UltronAttack.png",
    [Enemies.eTHANOS]: "IMG_ThanosAttack.png"
});

/** @enum {String} */
export const BossHurtSprite = Object.freeze({
    [Enemies.eLOKI_WEAKENED]: "IMG_LokiHurt.png",
    [Enemies.eLOKI]: "IMG_LokiHurt.png",
    [Enemies.eLOKI_COPY]: "IMG_LokiCloneHurt.png",
    [Enemies.eULTRON]: "IMG_UltronHurt.png",
    [Enemies.eTHANOS]: "IMG_ThanosHurt.png"
});

/** @enum {String} */
export const BossAllOutAttackSprite = Object.freeze({
    [Enemies.eLOKI_WEAKENED]: "IMG_LokiAllOut.png",
    [Enemies.eLOKI]: "IMG_LokiAllOut.png",
    [Enemies.eULTRON]: "IMG_UltronAllOut.png",
    [Enemies.eTHANOS]: "IMG_ThanosAllOut.png"
});

/** @enum {String} */
export const EnemyAbilities = Object.freeze({
    eNONE: "None",
    eADD_RANDOM: "AddRandom", // Rename to eBUBBLE_SPAWN
    eCHARGE_UP: "ChargeUp",
    eDEFEND: "Defend", // Rename to eSHIELD_BUBBLE
    eCHARIOT: "Chariot", // Rename to eDISTORT
    ePREEMPTIVE_STRIKE: "PreemptiveStrike",
    eULTRON_CONTROL: "UltronControl", // Rename to eSHOOTER_OVERDRIVE
    eVIBRANIUM_SHELL: "VibraniumShell", // Rename to ePROTECT
    eJAM: "Jam",
    eREPAIR: "Repair",

    eDUPLICATE: "Duplicate",
    eADD_RANDOM_LINE: "AddRandomLine", // Rename to eBUBBLE_WALL
    eMIMIC: "Mimic",
    eILLUSION: "Illusion",
    eDECOY: "DecoyManipulation",

    eCONCUSSIVE_BEAM: "ConcussiveBeam",
    eHACKER: "Hacker",
    eMASS_PRODUCTION: "MassProduction",
    eUPGRADE: "Upgrade",

    eHEAVY_SLAM: "HeavySlam", // Rename to eBUBBLE_EXPLOSION (Leviathan)
    eTROOPS_POD: "TroopsPod",

    ePROXIMAS_SPEAR: "ProximasSpear",
    eCORVUS_GLAIVE: "CorvusGlaive",
    eTELEKINESIS: "Telekinesis",
    eLEVITATION: "Levitation",
    eEXPERT_TACTICIAN: "ExpertTactician",
    eCHAIN_HAMMER: "ChainHammer",
    eDEFENSE_STANCE: "DefenseStance",
    eBUBBLE_PUSH: "BubblePush",
    eSTUN: "Stun",
    eREGENERATION: "Regeneration",

    eSNAP: "Snap",
    eDOUBLE_EDGED_SWORD: "Double-EdgedSword",
    eCRUSHING_POWER: "CrushingPower",
    eSPACE_MANIPULATION: "SpaceManipulation",
    eREWIND: "Rewind",
    eREALITY_SHIFT: "RealityShift",
    eMIND_MANIPULATION: "MindManipulation"
});

export const ENEMY_ABILITY_NAMES = Object.freeze({
    [EnemyAbilities.eADD_RANDOM]: "IDS_TXT_ENEMY_GENERIC_ABILITY_00_DESCRIPTION", // Rename to eBUBBLE_SPAWN
    [EnemyAbilities.eCHARGE_UP]: "IDS_TXT_ENEMY_GENERIC_ABILITY_01_DESCRIPTION",
    [EnemyAbilities.eDEFEND]: "IDS_TXT_ENEMY_GENERIC_ABILITY_02_DESCRIPTION", // Rename to eSHIELD_BUBBLE
    [EnemyAbilities.eCHARIOT]: "IDS_TXT_ENEMY_GENERIC_ABILITY_03_DESCRIPTION", // Rename to eDISTORT
    [EnemyAbilities.ePREEMPTIVE_STRIKE]: "IDS_TXT_ENEMY_GENERIC_ABILITY_04_DESCRIPTION",
    [EnemyAbilities.eULTRON_CONTROL]: "IDS_TXT_ENEMY_GENERIC_ABILITY_05_DESCRIPTION", // Rename to eSHOOTER_OVERDRIVE
    [EnemyAbilities.eVIBRANIUM_SHELL]: "IDS_TXT_ENEMY_GENERIC_ABILITY_06_DESCRIPTION", // Rename to ePROTECT
    [EnemyAbilities.eJAM]: "IDS_TXT_ENEMY_GENERIC_ABILITY_07_DESCRIPTION",
    [EnemyAbilities.eREPAIR]: "IDS_TXT_ENEMY_GENERIC_ABILITY_08_DESCRIPTION",

    [EnemyAbilities.eDUPLICATE]: "IDS_TXT_ENEMY_LOKI_ABILITY_00_DESCRIPTION",
    [EnemyAbilities.eADD_RANDOM_LINE]: "IDS_TXT_ENEMY_LOKI_ABILITY_01_DESCRIPTION", // Rename to eBUBBLE_WALL
    [EnemyAbilities.eMIMIC]: "IDS_TXT_ENEMY_LOKI_ABILITY_02_DESCRIPTION",
    [EnemyAbilities.eILLUSION]: "IDS_TXT_ENEMY_LOKI_ABILITY_03_DESCRIPTION",
    [EnemyAbilities.eDECOY]: "IDS_TXT_ENEMY_LOKI_ABILITY_04_DESCRIPTION",

    [EnemyAbilities.eCONCUSSIVE_BEAM]: "IDS_TXT_ENEMY_ULTRON_ABILITY_00_DESCRIPTION",
    [EnemyAbilities.eHACKER]: "IDS_TXT_ENEMY_ULTRON_ABILITY_01_DESCRIPTION",
    [EnemyAbilities.eMASS_PRODUCTION]: "IDS_TXT_ENEMY_ULTRON_ABILITY_02_DESCRIPTION",
    [EnemyAbilities.eUPGRADE]: "IDS_TXT_ENEMY_ULTRON_ABILITY_03_DESCRIPTION",

    [EnemyAbilities.eHEAVY_SLAM]: "IDS_TXT_ENEMY_LEVIATHAN_ABILITY_00_DESCRIPTION", // Rename to eBUBBLE_EXPLOSION (Leviathan)
    [EnemyAbilities.eTROOPS_POD]: "IDS_TXT_ENEMY_LEVIATHAN_ABILITY_01_DESCRIPTION",

    [EnemyAbilities.ePROXIMAS_SPEAR]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_00_DESCRIPTION",
    [EnemyAbilities.eCORVUS_GLAIVE]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_01_DESCRIPTION",
    [EnemyAbilities.eTELEKINESIS]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_02_DESCRIPTION",
    [EnemyAbilities.eLEVITATION]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_03_DESCRIPTION",
    [EnemyAbilities.eEXPERT_TACTICIAN]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_04_DESCRIPTION",
    [EnemyAbilities.eCHAIN_HAMMER]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_05_DESCRIPTION",
    [EnemyAbilities.eDEFENSE_STANCE]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_06_DESCRIPTION",
    [EnemyAbilities.eBUBBLE_PUSH]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_07_DESCRIPTION",
    [EnemyAbilities.eSTUN]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_08_DESCRIPTION",
    [EnemyAbilities.eREGENERATION]: "IDS_TXT_ENEMY_BLACK_ORDER_ABILITY_09_DESCRIPTION",

    [EnemyAbilities.eSNAP]: "IDS_TXT_ENEMY_THANOS_ABILITY_00_DESCRIPTION",
    [EnemyAbilities.eDOUBLE_EDGED_SWORD]: "IDS_TXT_ENEMY_THANOS_ABILITY_01_DESCRIPTION",
    [EnemyAbilities.eCRUSHING_POWER]: "IDS_TXT_ENEMY_THANOS_ABILITY_02_DESCRIPTION",
    [EnemyAbilities.eSPACE_MANIPULATION]: "IDS_TXT_ENEMY_THANOS_ABILITY_03_DESCRIPTION",
    [EnemyAbilities.eREWIND]: "IDS_TXT_ENEMY_THANOS_ABILITY_04_DESCRIPTION",
    [EnemyAbilities.eREALITY_SHIFT]: "IDS_TXT_ENEMY_THANOS_ABILITY_05_DESCRIPTION",
    [EnemyAbilities.eMIND_MANIPULATION]: "IDS_TXT_ENEMY_THANOS_ABILITY_06_DESCRIPTION"
});

export const ULTIMATE_ABILITIES = [
    EnemyAbilities.eDUPLICATE,
    EnemyAbilities.eSNAP,
    EnemyAbilities.eCONCUSSIVE_BEAM,
    EnemyAbilities.eREWIND
];

/** @enum {String} */
export const Cinematic = Object.freeze({
    eINTRO: "introPlayed",
    eENDING: "endingPlayed",
    eANY: "any"
});

/** @enum {Number} */
export const CinematicStep = Object.freeze({
    eSTART: 0,
    eTOWER_TEXT: 1,
    eLOKI: 2,
    eLOKI_TEXT: 3,
    eIRON_MAN: 4,
    eIRON_MAN_TEXT: 5,
    eAVENGERS: 6,
    eAVENGERS_TEXT: 7,
    eEND: 8
});

export const CINEMATIC_TIME_PER_LETTER = 50; // ms, time per letter when typing in the cinematic.
export const CINEMATIC_PANEL_APPEAR_TIME = 1000; // ms
export const CINEMATIC_PANEL_DISAPPEAR_TIME = 500; // ms
export const CINEMATIC_AVENGERS_APPEAR_TIME = 1800; // ms
export const CINEMATIC_AVENGERS_ASSEMBLE_APPEAR_TIME = 600; // ms

/** @enum {String} */
export const Tutorials = Object.freeze({
    eLVL_1: "Level1",
    eLVL_2: "Level2",
    eALL_OUT_ATTACK: "AllOutAttack",
    eHERO_SELECTION: "HeroSelection",
    eBUBBLE_HEAL: "BubbleHeal",
    eBUBBLE_HIDDEN: "BubbleHidden",
    eBUBBLE_CHAMELEON: "BubbleChameleon",
    eBUBBLE_STONE: "BubbleStone",
    eBUBBLE_COLORLESS: "BubbleColorless",
    eBUBBLE_BLOOMING: "BubbleBlooming",
    eBUBBLE_BLOOMING_STONE: "BubbleBloomingStone",
    eANY: "any"
});

/** @enum {Number} */
export const TutorialStep = Object.freeze({
    eFIRST: 0,
    eCHANGE_TEAM: 0,
    eSWITCH_AVENGERS: 1,
    eSAVE_TEAM: 2,
    eRELEASE_TO_SHOOT: 0,
    eCLEAR_BUBBLES: 1,
    eDEFEAT_ALL_ENEMIES: 2,
    eSWITCH_BUBBLES: 0,
    eMATCH_MISSED: 1,
    eMATCH_MADE: 2,
    eABILITY_INFO: 3,
    eABILITY_READY: 4
});

export const DEFAULT_HERO_TEAM = [Heroes.eHULK, Heroes.eCAPTAIN_MARVEL, Heroes.eCAPTAIN_AMERICA, Heroes.eIRON_MAN];
export const FIRST_LEVEL = 1;
export const LAST_LEVEL = 20;
export const LEVEL_ABILITIES = 2;
export const ENEMIES_BACK_FRAME = "FRM_EnemiesBottom";
export const ENEMIES_BACK_FRAME_DAMAGED = "FRM_EnemiesBottomHurt";

export const RESULT_VICTORY_ANIMATION_DELAY = 208;
export const RESULT_VICTORY_ANIMATION_TIME = 950;
export const RESULT_VICTORY_ANIMATION_START_SCALE = 0.01;

/** @enum {String} */
export const LevelData = Object.freeze({
    eSETTINGS: "LevelSettings",
    eWAVES: "WavesFolder",
    eBUBBLES: "Bubbles",
    eRESPAWN: "Respawn"
});

export const LEVEL_EDITOR_BUBBLE_X_OFFSET = 28;
export const LEVEL_EDITOR_BUBBLE_y_OFFSET = 32;
export const LEVEL_EDITOR_BUBBLE_GRID_OFFSET = 16;

/** @enum {String} */
export const ShooterState = Object.freeze({
    eUNDEFINED: "Undefined",
    eREADY: "Ready",
    eBUSY: "Busy",
    eSWAPPING: "Swapping"
});

/** @enum {Number} */
export const BubbleColor = Object.freeze({
    eUNDEFINED: -1,
    eNONE: -1,
    eRED: 0,
    eORANGE: 1,
    eYELLOW: 2,
    eGREEN: 3,
    eBLUE: 4,
    ePURPLE: 5,
    eSPECIAL: 6,
    eSTONE: 7,
    eCOLORLESS: 8,
    eLIGHTNING: 9,
    eBURST: 10,
    eMULTICOLOR: 11,
    ePIERCE: 12,
    eHEAL: 13,
    eSHIELDED: 14,
    eCHAMELEON: 15,
    eBLOOMING: 16,
    eSTONE_BLOOMING: 17,
    eRANDOM: 18, // Random with basic and special colors
    eRANDOM_EDITOR: 19, // Random with only the basic colors
    eEMPTY: 20,
    eMIMIC: 21 // Only use to tell the GameManager to use the color of the last shot bubble
});

export const BUBBLE_COLOR_NAME_TO_ID = {
    ["None"]: BubbleColor.eNONE,
    ["Red"]: BubbleColor.eRED,
    ["Orange"]: BubbleColor.eORANGE,
    ["Yellow"]: BubbleColor.eYELLOW,
    ["Green"]: BubbleColor.eGREEN,
    ["Blue"]: BubbleColor.eBLUE,
    ["Purple"]: BubbleColor.ePURPLE,
    ["Special"]: BubbleColor.eSPECIAL,
    ["Multicolor"]: BubbleColor.eMULTICOLOR,
    ["Pierce"]: BubbleColor.ePIERCE,
    ["Burst"]: BubbleColor.eBURST,
    ["Lightning"]: BubbleColor.eLIGHTNING,
    ["Colorless"]: BubbleColor.eCOLORLESS,
    ["Stone"]: BubbleColor.eSTONE,
    ["Heal"]: BubbleColor.eHEAL,
    ["Shielded"]: BubbleColor.eSHIELDED,
    ["Chameleon"]: BubbleColor.eCHAMELEON,
    ["Blooming"]: BubbleColor.eBLOOMING,
    ["StoneBlooming"]: BubbleColor.eSTONE_BLOOMING,
    ["Random"]: BubbleColor.eRANDOM
};

export const BUBBLE_COLOR_ID_TO_NAME = {
    [BubbleColor.eNONE]: "None",
    [BubbleColor.eRED]: "Red",
    [BubbleColor.eORANGE]: "Orange",
    [BubbleColor.eYELLOW]: "Yellow",
    [BubbleColor.eGREEN]: "Green",
    [BubbleColor.eBLUE]: "Blue",
    [BubbleColor.ePURPLE]: "Purple",
    [BubbleColor.eSPECIAL]: "Special",
    [BubbleColor.eMULTICOLOR]: "Multicolor",
    [BubbleColor.ePIERCE]: "Pierce",
    [BubbleColor.eBURST]: "Burst",
    [BubbleColor.eLIGHTNING]: "Lightning",
    [BubbleColor.eCOLORLESS]: "Colorless",
    [BubbleColor.eSTONE]: "Stone",
    [BubbleColor.eHEAL]: "Heal",
    [BubbleColor.eSHIELDED]: "Shielded",
    [BubbleColor.eCHAMELEON]: "Chameleon",
    [BubbleColor.eBLOOMING]: "Blooming",
    [BubbleColor.eSTONE_BLOOMING]: "StoneBlooming",
    [BubbleColor.eRANDOM]: "Random"
};

export const BUBBLE_COLOR_SPRITES = Object.freeze({
    [BubbleColor.eRED]: "IMG_DotRed",
    [BubbleColor.eORANGE]: "IMG_DotOrange",
    [BubbleColor.eYELLOW]: "IMG_DotYellow",
    [BubbleColor.eGREEN]: "IMG_DotGreen",
    [BubbleColor.eBLUE]: "IMG_DotBlue",
    [BubbleColor.ePURPLE]: "IMG_DotPurple",
    [BubbleColor.eSTONE]: "IMG_DotStone",
    [BubbleColor.eMULTICOLOR]: "IMG_DotMulticolor"
});

export const BUBBLE_COLOR_GRAPHIC_TO_ID = {
    ["None"]: BubbleColor.eNONE,
    ["Undefined"]: BubbleColor.eUNDEFINED,
    ["RedBubble"]: BubbleColor.eRED,
    ["OrangeBubble"]: BubbleColor.eORANGE,
    ["YellowBubble"]: BubbleColor.eYELLOW,
    ["GreenBubble"]: BubbleColor.eGREEN,
    ["BlueBubble"]: BubbleColor.eBLUE,
    ["PurpleBubble"]: BubbleColor.ePURPLE,
    ["MulticolorBubble"]: BubbleColor.eMULTICOLOR,
    ["PierceBubble"]: BubbleColor.ePIERCE,
    ["BurstBubble"]: BubbleColor.eBURST,
    ["LightningBubble"]: BubbleColor.eLIGHTNING,
    ["ColorlessBubble"]: BubbleColor.eCOLORLESS,
    ["StoneBubble"]: BubbleColor.eSTONE,
    ["HealBubble"]: BubbleColor.eHEAL,
    ["ShieldBubble"]: BubbleColor.eSHIELDED,
    ["ChameleonBubble"]: BubbleColor.eCHAMELEON,
    ["BloomingBubble"]: BubbleColor.eBLOOMING,
    ["StoneBloomingBubble"]: BubbleColor.eSTONE_BLOOMING,
    ["RandomBubble"]: BubbleColor.eRANDOM,
    ["Empty"]: BubbleColor.eEMPTY
};

export const BUBBLE_COLOR_ID_TO_GRAPHIC = {
    [BubbleColor.eNONE]: "None",
    [BubbleColor.eRED]: "RedBubble",
    [BubbleColor.eORANGE]: "OrangeBubble",
    [BubbleColor.eYELLOW]: "YellowBubble",
    [BubbleColor.eGREEN]: "GreenBubble",
    [BubbleColor.eBLUE]: "BlueBubble",
    [BubbleColor.ePURPLE]: "PurpleBubble",
    [BubbleColor.eMULTICOLOR]: "MulticolorBubble",
    [BubbleColor.ePIERCE]: "PierceBubble",
    [BubbleColor.eBURST]: "BurstBubble",
    [BubbleColor.eLIGHTNING]: "LightningBubble",
    [BubbleColor.eCOLORLESS]: "ColorlessBubble",
    [BubbleColor.eSTONE]: "StoneBubble",
    [BubbleColor.eHEAL]: "HealBubble",
    [BubbleColor.eSHIELDED]: "ShieldBubble",
    [BubbleColor.eCHAMELEON]: "ChameleonBubble",
    [BubbleColor.eBLOOMING]: "BloomingBubble",
    [BubbleColor.eSTONE_BLOOMING]: "StoneBloomingBubble",
    [BubbleColor.eRANDOM]: "RandomBubble",
    [BubbleColor.eRANDOM_EDITOR]: "RandomBubble"
};

/** @enum {String} */
export const BubblePopSource = Object.freeze({
    eMATCH: "Match",
    ePIERCE: "Pierce",
    eLIGHTNING: "Lightning",
    eDISCONNECT: "Disconnect"
});

/** @enum {String} */
export const BuffDebuff = Object.freeze({
    eALL_OUT: "AllOut",
    eCOUNTER: "Counter",
    eOVERTIME: "Overtime",
    eRANDOMIZED_SHOOTER: "RandomizedShooter",
    eSHIELD: "Shield",
    eSIZE_MANIPULATION: "SizeManipulation"
});

export const CHAMELEON_BUBBLE_IN_GAME_OVERLAY = "ChameleonOverlay";
export const HEAL_BUBBLE_IDLE_OVERLAY = "HealBubbleOverlay";
export const MULTICOLOR_BUBBLE_IN_GAME_OVERLAY = "BubbleIronManSwirl";
export const PIERCE_BUBBLE_IDLE_OVERLAY = "PierceBubbleOverlay";
export const PIERCE_BUBBLE_TRANSITION_OVERLAY = "PierceBubbleTransition";
export const PIERCE_BUBBLE_TRAIL_OVERLAY = "PierceBubbleTrail";
export const BURST_BUBBLE_IN_GAME_OVERLAY_1 = "BubbleBurstBackground1";
export const BURST_BUBBLE_IN_GAME_OVERLAY_2 = "BubbleBurstBackground2";

export const LIGHTNING_BUBBLE_OVERLAY = "ThorBubble";
export const LIGHTNING_BUBBLE_TRAIL_ANCHOR_X = 0.75;

export const MULTICOLOR_BUBBLE_TRANSITION_OVERLAY = "MulticolorBubbleTransition";
export const MULTICOLOR_BUBBLE_TRAIL_OVERLAY = "MulticolorBubbleTrail";
export const HP_BAR_DAMAGE_ANIMATION = "HPBarHit";
export const HERO_HEALING_ANIMATION = "HeroHealing";
export const HERO_POWER_UP_BUFF_ANIMATION = "HeroPowerUpBuff";
export const SHIELD_BUBBLE_OVERLAY = "ShieldBubbleOverlay";
export const BURST_BUBBLE_TRANSITION_OVERLAY = "BurstBubbleTransition";
export const BURST_BUBBLE_TRAIL_OVERLAY = "BurstBubbleTrail";
export const BLOOMING_BUBBLE_ANIMATED_OVERLAY = "BloomingBubbleAnimatedOverlay";
export const BLOOMING_BUBBLE_APPEAR_ANIMATION = "BubbleBloomingAppearAnimation";
export const STONE_BLOOMING_BUBBLE_ANIMATED_OVERLAY = "StoneBlooming";
export const STONE_BLOOMING_BUBBLE_APPEAR_ANIMATION = "StoneBloomingAppear";
export const BUBBLE_HERO_SPAWN_ANIMATION = "ANM_BubbleHeroesSpawn";

/** @type {BubbleColor[]} */
export const MULTICOLOR_VALUES = [
    BubbleColor.eRED,
    BubbleColor.eBLUE,
    BubbleColor.eGREEN,
    BubbleColor.eYELLOW,
    BubbleColor.eORANGE,
    BubbleColor.ePURPLE
];

/** @enum {String} */
export const GameState = Object.freeze({
    eUNDEFINED: "Undefined",
    eGAME_START: "GameStart",
    eSTART_WAVE: "StartWave",
    ePLAYER_TURN: "PlayerTurn",
    eFIRING_BUBBLE: "FiringBubble",
    eBUBBLE_CONTACT: "BubbleContact",
    eAPPLY_DAMAGE: "ApplyDamage",
    eENEMY_ATTACK: "EnemyAttack",
    eBUBBLE_CHANGES: "BubbleChanges",
    eBLACK_PANTHER_ATTACK: "BlackPantherAttack",
    eVICTORY: "Victory",
    eDEFEAT: "Defeat",
    eDONE: "Done"
});

/** @enum {Number} */
export const BubblePool = Object.freeze({
    eINVALID: -1,
    eGRID: 0,
    eCANNON: 1,
    eTOTAL_BUBBLE_POOL: 2
});

/** @enum {Number} */
export const HeroState = Object.freeze({
    eUNDEFINED: -1,
    eIDLE: 0,
    eATTACKING: 1,
    eDYING: 2,
    eDONE: 3,
    eNB_CHARACTER_STATES: 4
});

/** @enum {Number} */
export const EnemyState = Object.freeze({
    eUNDEFINED: -1,
    eIDLE: 0,
    eATTACKING: 1,
    eDAMAGED: 2,
    eDYING: 3,
    eDONE: 4,
    eNB_ENEMY_STATES: 5
});

/** @enum {Number} */
export const EvenOdd = Object.freeze({
    eUNDEFINED: -1,
    eEVEN: 0,
    eODD: 1,

    eNB_EVEN_ODD: 2
});

/** @enum {Number} */
export const DebugRequestType = Object.freeze({
    eUNDEFINED: -1,
    eTOGGLE_PHYSICAL_BODIES_DRAW: 0,
    eOFFSET_GRID: 2,
    eCLEAR_ALL_CONSOLE_LOGS: 3,
    eRESTART: 4,
    eCREATE_NEW_LINE: 5
});

export const WALL_TOP_Y = -239;
export const WALL_BOTTOM_Y = 259;
export const WALL_LEFT_X = -315;
export const WALL_RIGHT_X = 316;

export const BUBBLE_RADIUS = 16;
export const DEFAULT_BUBBLE_PHYSIC_RADIUS = 16;
export const DEFAULT_BUBBLE_SPEED = 950;
export const NB_NEIGHBOURS = 6;
export const DEFAULT_BASE_DAMAGE = 2;
export const DEFAULT_HERO_BASE_DAMAGE = 250;
export const DEFAULT_HEAL_BUBBLE_HEALING = 250;
export const DEFAULT_MAX_AIM_ANGLE = 80;
export const DEFAULT_VICTORY_ANIMATION_DURATION = 0.55;
export const DEFAULT_ENEMY_SHAKE_DURATION = 0.5;
export const DEFAULT_HERO_SHAKE_DURATION = 0.3;
export const DEFAULT_SHAKE_DISTANCE = 10;

export const MATHLINE_NEAR_ZERO_PRECISION = 0.005;

export const RESERVE_SCALE = 0.75;
export const RESERVE_X_POSITION = -265;
export const RESERVE_Y_POSITION = -19;
export const SHOOTER_X_POSITION = -237;
export const SHOOTER_Y_POSITION = 10;

export const AIMLINE_POSITION_X_OFFSET = -252;
export const AIMLINE_NB_DOTS = 50;
export const AIMLINE_NB_LASER_SEGMENTS = 40;
export const AIMLINE_NB_PATCHES = 12;
export const AIMLINE_LAST_LASER_SEGMENT_TRUNCATE = 16;
export const AIMLINE_DOTS_SPACING = 30;
export const AIMLINE_DOTS_SPEED = 100; // pixels per second
export const AIMLINE_DOTS_FADE_DISTANCE = 30;
export const AIMLINE_LASER_SEGMENT_LENGTH = 99;
export const AIMLINE_LASER_SEGMENT_THRESHOLD = 25;
export const AIMLINE_DOTS_SPRITES = [
    "IMG_ShooterRed",
    "IMG_ShooterOrange",
    "IMG_ShooterYellow",
    "IMG_ShooterGreen",
    "IMG_ShooterBlue",
    "IMG_ShooterPurple",
    "IMG_ShooterSpecial"
];
export const AIMLINE_TARGET_INDICATORS = [
    "IMG_ShooterIndicatorRed",
    "IMG_ShooterIndicatorOrange",
    "IMG_ShooterIndicatorYellow",
    "IMG_ShooterIndicatorGreen",
    "IMG_ShooterIndicatorBlue",
    "IMG_ShooterIndicatorPurple",
    "IMG_ShooterIndicatorSpecial"
];

export const FIRED_BUBBLE_COLLISION_LINE_LENGTH = 1000;

export const LAST_ATTACK_PARTICLE_ACKNOWLEDGEMENT_DELAY = 0.75; // seconds
export const NB_ATTACK_PARTICLES_SPRITES = 4;

export const BUBBLE_TINTS = {
    [BubbleColor.eRED]: 0xeb3834,
    [BubbleColor.eORANGE]: 0xff9c01,
    [BubbleColor.eYELLOW]: 0xfff678,
    [BubbleColor.eGREEN]: 0x3ff852,
    [BubbleColor.eBLUE]: 0x6de2ff,
    [BubbleColor.ePURPLE]: 0xb41bda,
    [BubbleColor.eSTONE]: 0xaaaaaa
};

export const NEIGHBORS_OFFSETS = [
    [
        { column: -1, row: -1 }, // Top-right
        { column: -1, row: 0 }, // Bottom-right
        { column: 0, row: -1 }, // Top
        { column: 0, row: 1 }, // Bottom
        { column: 1, row: -1 }, // Top-left
        { column: 1, row: 0 } // Bottom-left
    ],
    [
        { column: -1, row: 0 }, // Top-right
        { column: -1, row: 1 }, // Bottom-right
        { column: 0, row: -1 }, // Top
        { column: 0, row: 1 }, // Bottom
        { column: 1, row: 0 }, // Top-Left
        { column: 1, row: 1 } // Bottom-left
    ]
];

/** @enum {Number} */
export const nOffsetOrder = Object.freeze({
    eTOP_RIGHT: 0,
    eBOTTOM_RIGHT: 1,
    eTOP: 2,
    eBOTTOM: 3,
    eTOP_LEFT: 4,
    eBOTTOM_LEFT: 5
});

export const GEAR_MENU_X_POS = 50;
export const GEAR_MENU_Y_POS = 50;
export const LEVEL_SELECTION_SHAKE_DURATION = 200;
export const LEVEL_SELECTION_SHAKE_OFFSET_X = 8;
export const LEVEL_SELECTION_SHAKE_OFFSET_Y = 4;

export const LEVEL_SELECTION_ARROWS_OFFSET = 60;

export const HERO_SELECTION_NAVIGATION_AXIS_RATIO = 3;
export const HERO_SELECTION_TRANSITION_DURATION = 600;
export const HERO_SELECTION_PANEL_MASK_WIDTH = 1000;
export const HERO_SELECTION_PANEL_MASK_HEIGHT = 640;
export const HERO_SELECTION_DARKEN_OPACITY = 0.6;
export const HERO_SELECTION_SLIDING_START_X = -900;

export const HERO_SELECTION_DRAG_ANIMATION = {
    start: {
        x: -320,
        y: -120
    },
    end: {
        x: -430,
        y: -170
    },
    delayPress: 700,
    delayDrag: 500,
    durationDrag: 1000,
    delayRelease: 500
};

export const HERO_SELECTION_TAP_ANIMATION = {
    start: {
        x: -340,
        y: -110
    },
    end: {
        x: -460,
        y: -180
    },
    durationFadeIn: 600,
    delayFadeIn: 500,
    delaySelection: 600,
    durationFadeOut: 600,
    delayFadeOut: 500
};

export const ENEMY_FX_SMALL_DAMAGE_THRESHOLD = 0.3;
export const ENEMY_FX_MEDIUM_DAMAGE_THRESHOLD = 0.6;
export const ENEMY_HEAL_FX_OFFSET_X = 0;
export const ENEMY_HEAL_FX_OFFSET_Y = -8;

export const ENEMY_POSITIONS = [
    { x: 430, y: -180 },
    { x: 430, y: 0 },
    { x: 430, y: 180 }
];

export const DEFAULT_ENEMY_ATTACK_FLASH_COUNT = 5;
export const DEFAULT_ENEMY_ATTACK_FLASH_HIDE_DELAY = 20;
export const DEFAULT_ENEMY_ATTACK_FLASH_SHOW_DELAY = 100;
export const DEFAULT_ENEMY_ATTACK_SLIDE_DISTANCE = 25;
export const DEFAULT_ENEMY_ATTACK_SLIDE_DELAY = 200;
export const DEFAULT_DELAY_BETWEEN_ATTACKS = 400;
export const DEFAULT_SHOW_ABILITY_NAME_DELAY = 1000;
export const DEFAULT_DELAY_BETWEEN_MULTIPLE_ACTIONS = 500;
export const DEFAULT_DELAY_BETWEEN_MULTIPLE_ABILITIES = 800;
export const DEFAULT_NEW_WAVE_SLIDE_DISTANCE = 75;
export const DEFAULT_NEW_WAVE_SLIDE_TIME = 750;
export const DEFAULT_ENEMY_DEATH_SLIDE_TIME = 750;

export const ENEMY_ATTACK_PARTICLE_TARGET_X = -340;
export const ENEMY_ATTACK_PARTICLE_TARGET_X_CAPTAIN_AMERICA = -210;
export const ENEMY_ATTACK_PARTICLE_TARGET_Y = 0;
export const ENEMY_ATTACK_PARTICLE_TARGET_Y_RANGE = 120;
export const ENEMY_ATTACK_PARTICLE_TRAVEL_TIME = 500; // ms
export const ENEMY_ATTACK_PARTICLE_CURVE_VERTICAL_RANGE = 250;

export const ENEMY_ABILITY_PARTICLE_TRAVEL_TIME = 500; // ms
export const ENEMY_ABILITY_PARTICLE_CURVE_VERTICAL_RANGE = 160;
export const ENEMY_ABILITY_PARTICLE_ADD_LINE_TARGET_X = 310;
export const ENEMY_ABILITY_PARTICLE_HERO_TARGET_X = -410;
export const ENEMY_ABILITY_PARTICLE_HERO_TARGET_Y = [-200, -100, 100, 200];
export const ENEMY_ABILITY_PARTICLE_CANNON_TARGET_X = -250;
export const ENEMY_ABILITY_PARTICLE_CANNON_TARGET_Y = 0;

export const ABILITY_NAME_ROTATION = -5;

export const HERO_DAMAGE_LABEL_POSITION = {
    x: -290,
    y: -30
};

export const DEFAULT_HERO_DAMAGE_LABEL_SLIDE = 30;
export const DEFAULT_HERO_DAMAGE_LABEL_RANDOM_OFFSET = 10;
export const DEFAULT_HP_PER_SECOND = 50;
export const DEFAULT_CUT_IN_DELAY = 700;
export const DEFAULT_CUT_IN_MOVE_DURATION = 500;
export const DEFAULT_CUT_IN_STOP_DURATION = 1000;
export const DEFAULT_CUT_IN_TEXT_DELAY = 100;
export const DEFAULT_CUT_IN_X_DISTANCE = 512;
export const DEFAULT_CUT_IN_Y_DISTANCE = 100;
export const DEFAULT_CUT_IN_STOP_DISTANCE_X = 10;
export const DEFAULT_CUT_IN_STOP_DISTANCE_Y = 2;
export const DEFAULT_ALL_OUT_X_DISTANCE = 200;
export const DEFAULT_ALL_OUT_Y_DISTANCE = 640;

export const DEFAULT_ALL_OUT_DAMAGE_MULTIPLIER = 4;
export const DEFAULT_ALL_OUT_DAMAGE_DURATION = 4;

export const DEFAULT_FULL_DAMAGE = 1;
export const DEFAULT_RESPAWN_COLUMN_TIME = 200;
export const DEFAULT_BUBBLE_POP_TIME_MULTIPLIER = 1;
export const DEFAULT_RED_WARNING_LINE_PULSE = 1000;
export const DEFAULT_MULTICOLOR_BUBBLE_SPIN_TIME = 1000;
export const DEFAULT_CHAMELEON_BUBBLE_SPIN_TIME = 2000;

export const DEFAULT_SECOND_PARTICLE_DELAY = 100;
export const DEFAULT_THIRD_PARTICLE_DELAY = 150;
export const DEFAULT_FOURTH_PARTICLE_DELAY = 175;
export const DEFAULT_PARTICLE_TRAVELING_TIME = 500;
export const DEFAULT_PARTICLE_ROTATION = 3600;
export const DEFAULT_MAX_TIME_TO_POP_ALL_BUBBLES = 0.5;
export const DEFAULT_PARTICLE_CURVE_MULTIPLIER_1 = 150;
export const DEFAULT_PARTICLE_CURVE_MULTIPLIER_2 = 225;
export const DEFAULT_PARTICLE_SCALE = 0.7;
export const DEFAULT_WEAKNESS_DAMAGE_MULTIPLIER = 2;

export const DEFAULT_CLEAR_DISCONNECTED_DELAY = 100; // ms
export const DEFAULT_SCATTER_DELAY_MULTIPLIER = 1.5; // ms / px distance from contact point
export const DEFAULT_SCATTER_DELAY = 50; // ms
export const DEFAULT_SCATTER_DISTANCE = 80; // px
export const DEFAULT_SCATTER_DISTANCE_RANDOM_RANGE = 20; // px
export const DEFAULT_SCATTER_DURATION = 600; // ms
export const DEFAULT_SCATTER_POP_DELAY = 300; // ms
export const DEFAULT_SCATTER_DIRECTION_RANDOM_RANGE = 45; // degrees
export const DEFAULT_SCATTER_DIRECTION_MIN = 135; // degrees
export const DEFAULT_SCATTER_DIRECTION_MAX = 225; // degrees

export const DEFAULT_SPAWN_INITIAL_SCALE = 0.1;
export const DEFAULT_SPAWN_DURATION = 600; // ms
export const DEFAULT_SPAWN_CASCADE_DELAY = 100; // ms
export const DEFAULT_SPAWN_CASCADE_SPEEDUP = 10; // % per extra bubble

export const DEFAULT_AIMLINE_LENGTH = 1000;
export const DEFAULT_AIMLINE_REBOUND_LENGTH = 100;

export const LIGHTNING_SEGMENT_SIZE = 72;
export const LIGHTNING_CLEAR_DELAY = 1000;
export const DEFAULT_LIGHTNING_SPEED = 800; // pixels/sec
export const DEFAULT_LIGHTNING_SPEED_RANDOM_RANGE = 200; // Random range
export const DEFAULT_LIGHTNING_POP_DELAY = 400; // ms
export const DEFAULT_LIGHTNING_FADE_DELAY = 800; // ms
export const DEFAULT_THOR_ROTATION_SPEED = 720; // degrees / sec

export const BLACK_PANTHER_HARMFUL = 0;
export const BLACK_PANTHER_BASIC = 1;

// Default hero settings
export const DEFAULT_BLACK_PANTHER_MIN_DAMAGE = 1;
export const DEFAULT_BLACK_PANTHER_MAX_DAMAGE = 3;
export const DEFAULT_BLACK_PANTHER_DAMAGE_REDUCTION = 0;
export const DEFAULT_BLACK_PANTHER_DURATION = 3;
export const DEFAULT_BLACK_WIDOW_TIMER_INCREASE = 3;
export const DEFAULT_CAPTAIN_AMERICA_DURATION = 2;
export const DEFAULT_CAPTAIN_AMERICA_DAMAGE_REDUCTION = 100;
export const DEFAULT_WASP_DURATION = 1;
export const DEFAULT_WASP_DAMAGE_INCREASE = 2;
export const DEFAULT_CAPTAIN_MARVEL_PIERCE_COUNT = 10;

export const DEFAULT_BLOOMING_NBR = 5;
export const DEFAULT_BLOOMING_RADIUS = 2;
export const DEFAULT_BLOOMING_COUNTDOWN = 5;
export const DEFAULT_STONE_BLOOMING_NBR = 3;
export const DEFAULT_STONE_BLOOMING_RADIUS = 2;
export const DEFAULT_HEAL_BUBBLE_COUNTDOWN = 10;
export const DEFAULT_STONE_BLOOMING_COUNTDOWN = 5;

export const CANNON_RESERVE_MIN_HEIGHT = 50;

export const DEFAULT_CANNON_RELOAD_TIME = 500;
export const DEFAULT_CANNON_RESERVE_SPACING = 40;
export const DEFAULT_CANNON_EXTRA_VISIBLE_BUBBLES = 4;
export const DEFAULT_CANNON_RANDOM_BUBBLE_COLOR_CHANGE_TIMER = 1;

export const DEFAULT_AUDIO_BUBBLE_BURST_OVERLAP_DELAY = 50; // ms
export const DEFAULT_AUDIO_BUBBLE_BURST_OVERLAP_MAX_DELAY = 500; // ms

export const DEFAULT_BLACK_PANTHER_SHIELD_POS_X = -220; // pixels
export const DEFAULT_BLACK_PANTHER_SHOCKWAVE_POS_X = 300; // pixels
export const DEFAULT_BLACK_PANTHER_SHOCKWAVE_TIME = 800; // Duration in milliseconds of the shockwave animation
export const DEFAULT_BLACK_PANTHER_SHOCKWAVE_POP_TIME = 600; // Delay in milliseconds to pop bubbles after the shockwave animation begins

export const DEFAULT_DAMAGE_FONT_MAX_DAMAGE = 800;
export const DEFAULT_DAMAGE_FONT_NORMAL_DAMAGE = 30;
export const DEFAULT_DAMAGE_FONT_NORMAL_SIZE = 40;
export const DEFAULT_DAMAGE_FONT_MAX_SIZE_MULTIPLIER = 2.5;
export const DEFAULT_DAMAGE_FONT_MIN_SIZE_MULTIPLIER = 0.5;

export const CAPTAIN_AMERICA_SHIELD_SCALE_UP = 1.5;
export const CAPTAIN_AMERICA_SHIELD_SCALE_FINAL = 0.7;
export const CAPTAIN_AMERICA_SHIELD_FADE_TIME = 500; // ms

// Tutorial settings
export const TUTORIAL_NB_TURNS_TO_CHANGE = 3;
export const TUTORIAL_ARROW_ROTATION = -14;
export const TUTORIAL_DARKEN_OPACITY = 0.5;
export const TUTORIAL_INLINE_SPRITE_THRESHOLD = 2;
export const TUTORIAL_LABEL_WIDTH_RATIO = 0.9;
export const TUTORIAL_LABEL_STYLE = "Font_TextTuto";
export const TUTORIAL_LABEL_FONTSIZE = 22;
export const TUTORIAL_ABILITY_HAND_OFFSET = {
    x: -40,
    y: 5
};
export const DEFAULT_TUTORIAL_MESSAGE_WIDTH = 640;
export const DEFAULT_TUTORIAL_MESSAGE_HEIGHT = 100;

export const TUTORIAL_DARKEN_PADDING_SIZE = 1024;
export const TUTORIAL_DARKEN_PADDING_SCALE = 64;

export const TUTORIAL_MASKS = {
    [Tutorials.eLVL_1]: [null, "MaskTutorialBasic2", null],
    [Tutorials.eLVL_2]: ["MaskTutorialAbilities1", "MaskTutorialAbilities2", "MaskTutorialAbilities3", "MaskTutorialAbilities4", null],
    [Tutorials.eHERO_SELECTION]: ["MaskTutorialHeroes1", "MaskTutorialHeroes2", null]
};

// TODO : Move animation timing delays to the level editor
export const TEMP_BLOOMING_SPAWN_DELAY = 30; // ms
export const TEMP_DELAY_POP_BURST_BUBBLE = 200; // ms
export const TEMP_DELAY_CHANGE_CHAMELEON_COLOR = 200; // ms

export const TEMP_PATH_APPEARANCE_TIME = 1000; // ms
export const TEMP_CUT_OUT_TEXT_DISTANCE_MULTIPLIER = 2;

// TODO : Consider moving elsewhere?
export const shuffle = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
};
